import React, { useState, useEffect } from 'react';
import { MessageSquare, ThumbsUp, Star, Lightbulb, PenTool, Sparkles, Award, ChevronLeft, ChevronRight, ChevronDown, Gift } from 'lucide-react';
import { Button } from './common/Button';
import userFeedbackService, { FeedbackCategory, InterviewFeedbackCategory, FeedbackSubmissionResponse } from '../services/userFeedbackService';
import { Modal } from './common/Modal';

interface FeedbackOption {
  value: string;
  label: string;
}

const feedbackOptions: Record<InterviewFeedbackCategory, FeedbackOption[]> = {
  [InterviewFeedbackCategory.USABILITY]: [
    { value: 'Excellent - Very intuitive and easy to use', label: 'Excellent - Very intuitive and easy to use' },
    { value: 'Good - Mostly easy to navigate', label: 'Good - Mostly easy to navigate' },
    { value: 'Average - Could use some improvements', label: 'Average - Could use some improvements' },
    { value: 'Poor - Difficult to navigate', label: 'Poor - Difficult to navigate' },
    { value: 'OTHER', label: 'Other (Please specify)' }
  ],
  [InterviewFeedbackCategory.EXPERIENCE]: [
    { value: 'Very realistic - Just like a real interview', label: 'Very realistic - Just like a real interview' },
    { value: 'Somewhat realistic - Close to a real interview', label: 'Somewhat realistic - Close to a real interview' },
    { value: 'Neutral - Neither realistic nor unrealistic', label: 'Neutral - Neither realistic nor unrealistic' },
    { value: 'Somewhat unrealistic - Not very close to a real interview', label: 'Somewhat unrealistic - Not very close to a real interview' },
    { value: 'OTHER', label: 'Other (Please specify)' }
  ],
  [InterviewFeedbackCategory.REPORT]: [
    { value: 'Very helpful - Provided valuable insights', label: 'Very helpful - Provided valuable insights' },
    { value: 'Helpful - Gave useful feedback', label: 'Helpful - Gave useful feedback' },
    { value: 'Somewhat helpful - Some useful information', label: 'Somewhat helpful - Some useful information' },
    { value: 'Not helpful - Didn\'t provide valuable insights', label: 'Not helpful - Didn\'t provide valuable insights' },
    { value: 'OTHER', label: 'Other (Please specify)' }
  ]
};

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFeedbackSubmitted: () => void;
  onSkip?: () => void;
}

interface FeedbackState {
  category: InterviewFeedbackCategory;
  value: string;
  otherValue?: string;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ 
  isOpen, 
  onClose, 
  onFeedbackSubmitted,
  onSkip
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [rewardInfo, setRewardInfo] = useState<{ message: string; minutes: number } | null>(null);
  
  const [feedbacks, setFeedbacks] = useState<FeedbackState[]>([
    { category: InterviewFeedbackCategory.USABILITY, value: '' },
    { category: InterviewFeedbackCategory.EXPERIENCE, value: '' },
    { category: InterviewFeedbackCategory.REPORT, value: '' }
  ]);
  
  // Trigger animation when step changes
  useEffect(() => {
    setAnimate(false);
    const timer = setTimeout(() => setAnimate(true), 50);
    return () => clearTimeout(timer);
  }, [currentStep]);
  
  
  const categories = [
    { category: InterviewFeedbackCategory.USABILITY, title: 'Rate usability of Product', icon: <PenTool size={24} className="text-blue-500" />, description: 'How easy was it to use our platform?' },
    { category: InterviewFeedbackCategory.EXPERIENCE, title: 'How was your interview Experience', icon: <Star size={24} className="text-amber-500" />, description: 'Did the interview feel realistic and valuable?' },
    { category: InterviewFeedbackCategory.REPORT, title: 'How Good is the Interview Report/Feedback', icon: <Lightbulb size={24} className="text-purple-500" />, description: 'Was the feedback helpful for your preparation?' }
  ];
  
  const updateFeedback = (value: string, otherValue?: string) => {
    const newFeedbacks = [...feedbacks];
    newFeedbacks[currentStep] = {
      ...newFeedbacks[currentStep],
      value,
      otherValue
    };
    setFeedbacks(newFeedbacks);
  };
  
  const handleNext = () => {
    if (currentStep < categories.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      submitAllFeedback();
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const submitAllFeedback = async () => {
    try {
      setIsSubmitting(true);
      
      // Filter out any empty feedback
      const validFeedbacks = feedbacks.filter(f => f.value);
      
      if (validFeedbacks.length === 0) {
        // No valid feedback to submit
        setIsSubmitting(false);
        return;
      }
      
      // Submit each feedback category separately and check for rewards
      let firstTimeReward: { message: string; minutes: number } | null = null;
      
      for (const feedback of validFeedbacks) {
        const response = await userFeedbackService.submitFeedback({
          content: feedback.value,
          category: feedback.category as unknown as FeedbackCategory
        }) as FeedbackSubmissionResponse;
        
        // Check if this submission earned a reward (only capture the first one)
        if (!firstTimeReward && response.rewardInfo?.isFirstFeedback && response.rewardInfo.rewardMinutes && response.rewardInfo.message) {
          firstTimeReward = {
            message: response.rewardInfo.message,
            minutes: response.rewardInfo.rewardMinutes
          };
        }
      }
      
      // Set reward info if earned
      if (firstTimeReward) {
        setRewardInfo(firstTimeReward);
      }
      
      setFeedbackSubmitted(true);
      onFeedbackSubmitted();
      
      // Close the modal after a longer delay if there's a reward message
      setTimeout(() => {
        onClose();
      }, firstTimeReward ? 4000 : 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  const currentCategory = categories[currentStep];
  const currentFeedback = feedbacks[currentStep];
  const options = feedbackOptions[currentCategory.category as InterviewFeedbackCategory];
  const showOtherInput = currentFeedback.value === 'OTHER';
  
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={feedbackSubmitted ? "Thank You!" : "Share Your Feedback"}
      maxWidth="550px"
      hideCloseButton={true}
      preventOutsideClick={true}
    >
      <div className="p-6 relative overflow-hidden animate-fade-in">
        {feedbackSubmitted ? (
          <div className="flex flex-col items-center justify-center py-6">
            <div className="w-24 h-24 mb-6 relative">
              <div className="absolute inset-0 rounded-full bg-green-100 dark:bg-green-900 animate-ping opacity-75"></div>
              <div className="relative flex items-center justify-center w-full h-full rounded-full bg-green-100 dark:bg-green-900">
                {rewardInfo ? (
                  <Gift className="w-12 h-12 text-green-500 animate-bounce" />
                ) : (
                  <Award className="w-12 h-12 text-green-500 animate-bounce" />
                )}
              </div>
            </div>
            <h3 className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark mb-3">
              {rewardInfo ? 'Congratulations!' : 'We Appreciate Your Feedback!'}
            </h3>
            {rewardInfo && (
              <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg border border-green-200 dark:border-green-700">
                <div className="flex items-center justify-center mb-2">
                  <Gift className="w-6 h-6 text-green-500 mr-2" />
                  <span className="font-semibold text-green-700 dark:text-green-300">
                    +{rewardInfo.minutes} Free Minutes!
                  </span>
                </div>
                <p className="text-sm text-center text-green-600 dark:text-green-400">
                  {rewardInfo.message}
                </p>
              </div>
            )}
            <p className="text-center text-text-secondary-light dark:text-text-secondary-dark">
              Your insights help us improve Mocktrail for everyone. Thank you for taking the time to share your thoughts!
            </p>
          </div>
        ) : (
          <>
            <div className="mb-2">
              <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-4">
                Your feedback helps us improve the practice experience. You get <span className="font-semibold text-text-primary-light dark:text-text-primary-dark"> {process.env.REACT_APP_FEEDBACK_FREE_MINUTES || "10"} minutes of interview free</span> for one-time on giving feedback.
              </p>
            </div>
            
            <div className={`mb-6 transition-all duration-500 ${animate ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10">
                  {currentCategory.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-lg text-text-primary-light dark:text-text-primary-dark">
                    {currentCategory.title}
                  </h4>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    {currentCategory.description}
                  </p>
                </div>
              </div>
              
              <div>
                <div className="relative">
                <ChevronDown size={18} className={`absolute right-3 top-1/2 -translate-y-1/2 ${showOtherInput ? 'mt-0' : 'mt-0'} text-text-secondary-light dark:text-text-secondary-dark`} />
                <select
                  className="w-full p-3 rounded-lg bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark text-base text-text-primary-light dark:text-text-primary-dark focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none"
                  value={currentFeedback.value}
                  onChange={(e) => updateFeedback(e.target.value)}
                >
                  <option value="">Select an option</option>
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                </div>
                {showOtherInput && (
                  <div className="mt-4 relative rounded-lg overflow-hidden group text-base bg-background-light/50 dark:bg-background-dark/50 border border-border-light dark:border-border-dark text-text-primary-light dark:text-text-primary-dark focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:outline-none">
                    <textarea
                      className="w-full p-3 rounded-lg relative z-10"
                      placeholder="Please share your thoughts..."
                      value={currentFeedback.otherValue || ''}
                      onChange={(e) => updateFeedback('OTHER', e.target.value)}
                      rows={3}
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row gap-4 md:gap-0 justify-between items-center">
            <div className="flex space-x-2 justify-start">
                  {categories.map((_, index) => (
                    <div 
                      key={index} 
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-500 ${
                        index === currentStep 
                          ? 'bg-primary-light dark:bg-primary-dark scale-125' 
                          : index < currentStep
                            ? 'bg-green-500 dark:bg-green-400'
                            : 'bg-border-light dark:bg-border-dark'
                      }`}
                    />
                  ))}
              </div>
              
              <div className="flex w-full md:w-auto items-center space-x-4 justify-end">
              {currentStep > 0 ? (
                <Button
                  variant="outline"
                  className="w-full md:w-auto"
                  onClick={handlePrevious}
                  disabled={isSubmitting}
                  leftIcon={<ChevronLeft size={16} />}
                >
                  Previous
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={onSkip}
                  className="w-full md:w-auto text-text-secondary-light dark:text-text-secondary-dark"
                >
                  Skip
                </Button>
              )}
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={isSubmitting || !currentFeedback.value || (showOtherInput && !currentFeedback.otherValue)}
                  rightIcon={isSubmitting ? <Sparkles size={16} className="animate-pulse" /> : <ChevronRight size={16} />}
                  className="w-full md:w-auto relative overflow-hidden group"
                >
                  <span className="relative z-10">
                    {isSubmitting ? 'Submitting...' : currentStep < categories.length - 1 ? 'Next' : 'Submit'}
                  </span>
                </Button>
            </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default FeedbackModal; 